import { Params } from '@angular/router';
import { ConversionComparison, ConversionMetricsRequest } from './conversion-metrics.model';
import { DashboardPeriodEcho } from './dashboard-filters.model';
import { DashboardLink } from './dashboard-link.model';
import { DashboardVm } from './dashboard-vm.model';
import { PipelineRatio } from './pipeline-health.model';

/** Wire values; the adapter drops any other. */
export enum SmartInsightType {
  DOCUMENTS_WAITING = 'documents-waiting',
  APPROVAL_RATE = 'approval-rate',
  BEST_MONTH = 'best-month',
  TEAM_SEATS = 'team-seats',
}

export enum SmartInsightTone {
  WARNING = 'warning',
  SUCCESS = 'success',
  NEUTRAL = 'neutral',
}

export interface DocumentsWaitingInsight {
  applications: number;
  thresholdDays: number;
}

/** What the approval-rate row says; the adapter drops any other. */
export enum ApprovalRateState {
  MOVED = 'moved',
  STEADY = 'steady',
  INSUFFICIENT = 'insufficient',
}

export interface ApprovalRateComparedInsight {
  state: ApprovalRateState.MOVED | ApprovalRateState.STEADY;
  current: PipelineRatio;
  prior: PipelineRatio;
  deltaPoints: number;
  /** The whole team's current rate under My deals; null under Whole team. */
  team: PipelineRatio | null;
  comparison: ConversionComparison;
}

/** Too few submissions in either window: no rate to show. */
export interface ApprovalRateInsufficientInsight {
  state: ApprovalRateState.INSUFFICIENT;
  /** The configured floor both windows must reach. */
  minSubmitted: number;
  comparison: ConversionComparison;
}

export type ApprovalRateInsight = ApprovalRateComparedInsight | ApprovalRateInsufficientInsight;

export interface BestMonthInsight {
  monthToDateAmount: number;
  /** `YYYY-MM`, an Eastern month. */
  bestMonth: string;
  bestAmount: number;
}

export interface TeamSeatsInsight {
  seats: number;
  /** The shortest idle among the flagged seats. */
  daysIdle: number;
}

export type SmartInsightLink = DashboardLink;

interface SmartInsightBase {
  tone: SmartInsightTone;
  link: SmartInsightLink;
}

export interface DocumentsWaitingSmartInsight extends SmartInsightBase {
  type: SmartInsightType.DOCUMENTS_WAITING;
  documentsWaiting: DocumentsWaitingInsight;
}

export interface ApprovalRateSmartInsight extends SmartInsightBase {
  type: SmartInsightType.APPROVAL_RATE;
  approvalRate: ApprovalRateInsight;
}

export interface BestMonthSmartInsight extends SmartInsightBase {
  type: SmartInsightType.BEST_MONTH;
  bestMonth: BestMonthInsight;
}

export interface TeamSeatsSmartInsight extends SmartInsightBase {
  type: SmartInsightType.TEAM_SEATS;
  teamSeats: TeamSeatsInsight;
}

export type SmartInsight =
  | DocumentsWaitingSmartInsight
  | ApprovalRateSmartInsight
  | BestMonthSmartInsight
  | TeamSeatsSmartInsight;

export interface SmartInsights {
  asOfUtc: Date;
  period: DashboardPeriodEcho;
  /** In the server's order; empty is the empty state. */
  insights: SmartInsight[];
}

/** The conversion page's wire shape, unchanged. */
export type SmartInsightsRequest = ConversionMetricsRequest;

export interface SmartInsightRowVm {
  type: SmartInsightType;
  tone: SmartInsightTone;
  icon: string;
  headline: string;
  subline: string;
  ctaLabel: string;
  route: string;
  queryParams: Params;
}

export interface SmartInsightsDataVm {
  rows: SmartInsightRowVm[];
  isEmpty: boolean;
}

export type SmartInsightsVm = DashboardVm<SmartInsightsDataVm>;
